import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { storeAuthAPI } from '../services/storeAPI';

interface StoreUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  role_display: string;
  is_store_staff: boolean;
  can_access_admin_portal?: boolean;
  can_access_mill_portal?: boolean;
  call_panel_position?: { x: number; y: number } | null;
}

interface StoreAuthContextType {
  user: StoreUser | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  // Update the incoming-call panel position locally after a drag saves it server-side, so
  // the NEXT call in the same session opens where the user left it (no me() re-fetch).
  setCallPanelPosition: (pos: { x: number; y: number }) => void;
}

const StoreAuthContext = createContext<StoreAuthContextType | undefined>(undefined);

export const StoreAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<StoreUser | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Check auth by calling /api/auth/me/ (cookies sent automatically)
    // Store uses shared team login, so we use the main auth endpoint
    // Timeout: if auth check takes >5s, bail to login
    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Auth check timeout')), 5000)
    );
    try {
      const userData = await Promise.race([storeAuthAPI.me(), timeout]);

      // Convert to StoreUser interface
      const portals = userData.portals || [];
      const isSuperuser = userData.is_superuser === true;

      const storeUser: StoreUser = {
        id: userData.id,
        username: userData.username || userData.email,
        email: userData.email,
        first_name: userData.first_name || '',
        last_name: userData.last_name || '',
        role: userData.role || 'STAFF',
        role_display: userData.role_display || userData.role || 'Staff',
        is_store_staff: true,
        can_access_admin_portal: isSuperuser || portals.includes('admin'),
        can_access_mill_portal: isSuperuser || portals.includes('mill'),
        call_panel_position: userData.call_panel_position ?? null,
      };

      setUser(storeUser);
      return true;
    } catch (error) {
      setUser(null);
      return false;
    }
  }, []);

  useEffect(() => {
    // Initial auth check - run ONCE on mount only
    const doInitialCheck = async () => {
      await checkAuth();
      setLoading(false);
    };

    doInitialCheck();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    try {
      // Login endpoint sets httpOnly cookies automatically
      const response = await storeAuthAPI.login(username, password);
      const { user: userData } = response;

      setUser(userData);
    } catch (error) {
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    await storeAuthAPI.logout();
    setUser(null);
  }, []);

  const setCallPanelPosition = useCallback((pos: { x: number; y: number }) => {
    setUser((u) => (u ? { ...u, call_panel_position: pos } : u));
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    setCallPanelPosition,
  }), [user, loading, login, logout, setCallPanelPosition]);

  return (
    <StoreAuthContext.Provider value={value}>
      {children}
    </StoreAuthContext.Provider>
  );
};

export const useStoreAuth = () => {
  const context = useContext(StoreAuthContext);
  if (!context) {
    throw new Error('useStoreAuth must be used within StoreAuthProvider');
  }
  return context;
};
